import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Table
} from 'react-bootstrap';
import EmployeeAvatar from 'components/base/EmployeeGravatar';
import { toast } from 'react-hot-toast';
import { updateProfile } from 'services/api/profile';
import { useForm } from 'react-hook-form';
import PhoenixLoader from 'components/common/PhoenixLoader';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GET_ME, UPLOAD_PROFILE_IMAGE } from 'services/api/employee';
import { Employee } from 'data/users';
import { GET_JOBSTAGE_QUERY, JobStage } from 'services/api/jobStage';
import { UserFormData, JobStageNotificationPreference } from './types/types';

const STAGE_PREF_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'email_only', label: 'Email only' },
  { value: 'sms_only', label: 'SMS only' },
  { value: 'both', label: 'Both Email and SMS' }
];

const UserProfile = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [stagePreferences, setStagePreferences] = useState<
    JobStageNotificationPreference[]
  >([]);

  const { data: userData, isLoading: userDataLoading } = useQuery<Employee>(
    'user_profile_data',
    async () => {
      const response = await GET_ME();
      return response.data;
    }
  );

  const { data: jobStages } = useQuery(
    'job_stages_profile',
    GET_JOBSTAGE_QUERY,
    {
      staleTime: 60000
    }
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue
  } = useForm<UserFormData>({
    mode: 'onChange'
  });

  const updateProfileMutation = useMutation(
    (data: any) => updateProfile(data),
    {
      onSuccess: () => {
        toast.success('Profile updated successfully');
        queryClient.invalidateQueries('user_profile_data');
      },
      onError: error => {
        toast.error('Failed to update profile');
        console.error('Update profile error:', error);
      }
    }
  );

  const uploadImageMutation = useMutation(
    (file: File) => UPLOAD_PROFILE_IMAGE(file),
    {
      onSuccess: response => {
        toast.success('Profile image uploaded successfully');
        const imageUrl =
          typeof response === 'string'
            ? response
            : response?.profileImage ||
              response?.url ||
              response?.data?.profileImage ||
              response?.data?.url;

        if (imageUrl) {
          setValue('profileImage', imageUrl);
          queryClient.invalidateQueries('user_profile_data');
          queryClient.invalidateQueries('auth_me');
        } else {
          console.error('No image URL found in response:', response);
          toast.error('Uploaded image URL not received');
        }
      },
      onError: error => {
        toast.error('Failed to upload image');
        console.error('Image upload error:', error);
      }
    }
  );

  useEffect(() => {
    if (userData) {
      reset({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phoneNumber || '',
        info: '',
        newPassword: '',
        confirmPassword: '',
        username: userData.username || '',
        profileImage: userData.profileImage || '',
        mentionNotificationPreference:
          userData.mentionNotificationPreference || 'email_only',
        taskNotificationPreference:
          userData.taskNotificationPreference || 'email_only',
        jobWatchNotificationPreference:
          userData.jobWatchNotificationPreference || 'email_only',
        smsConsent: userData.smsConsent ? 'true' : 'false'
      });
      setStagePreferences(userData.jobStageNotificationPreferences || []);
    }
  }, [userData, reset]);

  const getStagePref = (stageId: string): string => {
    const found = stagePreferences.find(p => p.stageId === stageId);
    return found ? found.preference : 'none';
  };

  const handleStagePrefChange = (stageId: string, preference: string) => {
    setStagePreferences(prev => {
      const existing = prev.find(p => p.stageId === stageId);
      if (existing) {
        return prev.map(p =>
          p.stageId === stageId ? { ...p, preference } : p
        );
      }
      return [...prev, { stageId, preference }];
    });
  };

  const onSubmit = async (data: UserFormData) => {
    const smsConsentBoolean = String(data.smsConsent) === 'true';
    const payload = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phone,
      username: data.username,
      mentionNotificationPreference: data.mentionNotificationPreference,
      taskNotificationPreference: data.taskNotificationPreference,
      smsConsent: smsConsentBoolean,
      jobWatchNotificationPreference: data.jobWatchNotificationPreference,
      jobStageNotificationPreferences: stagePreferences.filter(
        p => p.preference !== 'none'
      ),
      ...(data.newPassword && { password: data.newPassword })
    };

    updateProfileMutation.mutate(payload);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadImageMutation.mutate(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (userDataLoading) return <PhoenixLoader />;

  const user = userData;

  if (!user) return <PhoenixLoader />;

  return (
    <Container fluid className="py-4 px-0">
      <Row>
        {/* Left Column - Profile Card and SMS Consent */}
        <Col lg={4} md={4} className="mb-4">
          <Card className="profile-card">
            <Card.Body>
              <div className="text-center mb-4">
                <div className="d-flex justify-content-center mb-2 position-relative">
                  {uploadImageMutation.isLoading ? (
                    <div className="position-relative">
                      <EmployeeAvatar src={user.email} size="xl" />
                      <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-light bg-opacity-75 rounded-circle">
                        <PhoenixLoader />
                      </div>
                    </div>
                  ) : (
                    <div
                      className="position-relative"
                      onClick={triggerFileInput}
                      style={{ cursor: 'pointer' }}
                    >
                      <EmployeeAvatar
                        src={user.profileImage || user.email}
                        size="xl"
                      />
                      <div className="position-absolute bottom-0 end-0">
                        <Button
                          size="sm"
                          variant="primary"
                          className="rounded-circle p-1"
                        >
                          <i className="fas fa-camera"></i>
                        </Button>
                      </div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="d-none"
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </div>
                  )}
                </div>
                <small className="text-muted d-block mt-1 mb-3">
                  Click on image to update
                </small>
                <div className="profile-info text-center">
                  <h3 className="mb-1">{`${user.firstName} ${user.lastName}`}</h3>
                  <p className="text-muted mb-3">@{user.username}</p>
                </div>
              </div>

              <div className="mt-4">
                <h6>User Role</h6>
                <Form.Check
                  type="radio"
                  label="Admin"
                  name="userRole"
                  id="admin"
                  checked={user.isAdmin}
                  disabled
                  className="mb-2"
                />
                <Form.Check
                  type="radio"
                  label="Employee"
                  name="userRole"
                  id="employee"
                  checked={!user.isAdmin}
                  disabled
                />
              </div>
            </Card.Body>
          </Card>

          {/* SMS Consent Section - now below profile card in left column */}
          <Card className="mb-4 mt-3">
            <Card.Body>
              <h4 className="mb-4">SMS Consent</h4>
              <Row>
                <Col xs={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>SMS NOTIFICATIONS</Form.Label>
                    <div className="mb-2">
                      <Form.Check
                        type="radio"
                        label="I consent to receive SMS notifications from Hole Opener Corp"
                        id="smsYes"
                        value="true"
                        {...register('smsConsent')}
                        className="mb-2"
                      />
                      <Form.Check
                        type="radio"
                        label="I do not consent to receive SMS notifications from Hole Opener Corp"
                        id="smsNo"
                        value="false"
                        {...register('smsConsent')}
                        className="mb-2"
                      />
                    </div>
                    <Form.Text className="text-muted">
                      "I agree to receive SMS notifications from Hole Opener
                      Corp related to tasks, updates, and job notifications."
                      Standard message rates may apply.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column - Forms */}
        <Col lg={8} md={8}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            {/* Personal Information Section */}
            <Card className="mb-4">
              <Card.Body>
                <h4 className="mb-4">Personal Information</h4>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>FIRST NAME</Form.Label>
                      <Form.Control type="text" {...register('firstName')} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>LAST NAME</Form.Label>
                      <Form.Control type="text" {...register('lastName')} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>EMAIL</Form.Label>
                      <Form.Control
                        type="email"
                        {...register('email')}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>PHONE NUMBER</Form.Label>
                      <Form.Control type="tel" {...register('phone')} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>USERNAME</Form.Label>
                      <Form.Control type="text" {...register('username')} />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>INFO</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        {...register('info')}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Notification Preferences Section */}
            <Card className="mb-4">
              <Card.Body>
                <h4 className="mb-4">Notification Preferences</h4>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Comment Mention Notifications</Form.Label>
                      <Form.Select
                        {...register('mentionNotificationPreference')}
                      >
                        <option value="none">
                          No Email or SMS (Inapp notifications always on)
                        </option>
                        <option value="email_only">Email only</option>
                        <option value="sms_only">SMS only</option>
                        <option value="both">Both Email and SMS</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Choose how you want to be notified when mentioned
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Task Notifications</Form.Label>
                      <Form.Select {...register('taskNotificationPreference')}>
                        <option value="none">
                          No Email or SMS (Inapp notifications always on)
                        </option>
                        <option value="email_only">Email only</option>
                        <option value="sms_only">SMS only</option>
                        <option value="both">Both Email and SMS</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Choose how you want to be notified about task updates
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Watched Job Notifications</Form.Label>
                      <Form.Select
                        {...register('jobWatchNotificationPreference')}
                      >
                        <option value="none">
                          No Email or SMS (Inapp notifications always on)
                        </option>
                        <option value="email_only">Email only</option>
                        <option value="sms_only">SMS only</option>
                        <option value="both">Both Email and SMS</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        How to be notified when a watched job changes stages
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-3" />
                <h5 className="mb-1">Job Stage Notifications</h5>
                <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                  Choose how to be notified when any job moves into a specific
                  stage.
                </p>
                {!jobStages?.data?.length ? (
                  <p className="text-muted">No stages found.</p>
                ) : (
                  <Table borderless size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '50%' }}>Stage</th>
                        <th>Notify me via</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobStages.data.map((stage: JobStage) => (
                        <tr key={stage.id}>
                          <td className="align-middle">{stage.name}</td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={getStagePref(stage.id)}
                              onChange={e =>
                                handleStagePrefChange(stage.id, e.target.value)
                              }
                              style={{ maxWidth: 220 }}
                            >
                              {STAGE_PREF_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {/* Change Password Section */}
            <Card className="mb-4">
              <Card.Body>
                <h4 className="mb-4">Change Password</h4>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>NEW PASSWORD</Form.Label>
                      <Form.Control
                        type="password"
                        {...register('newPassword', {
                          minLength: {
                            value: 8,
                            message: 'Password must be at least 8 characters'
                          },
                          onChange: e => {
                            return e.target.value;
                          }
                        })}
                      />
                      {errors.newPassword && (
                        <Form.Text className="text-danger">
                          {errors.newPassword.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>CONFIRM PASSWORD</Form.Label>
                      <Form.Control
                        type="password"
                        {...register('confirmPassword', {
                          validate: value =>
                            value === watch('newPassword') ||
                            'Passwords do not match',
                          onChange: e => {
                            return e.target.value;
                          }
                        })}
                      />
                      {errors.confirmPassword && (
                        <Form.Text className="text-danger">
                          {errors.confirmPassword.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Account Deletion Section */}
            <Card className="mb-4">
              <Card.Body>
                <h4>Account Deletion</h4>
                <p className="text-muted">
                  Transfer this account to another person or to a company
                  repository.
                </p>
                <Button variant="danger" disabled>
                  Delete account
                </Button>
              </Card.Body>
            </Card>

            {/* Form Actions */}
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => reset()}
                disabled={updateProfileMutation.isLoading}
              >
                Cancel Changes
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={updateProfileMutation.isLoading}
              >
                {updateProfileMutation.isLoading
                  ? 'Saving...'
                  : 'Save Information'}
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default UserProfile;
